import { useEffect, useMemo, useRef } from 'react';
import { useOrg } from './useOrg';
import { useUserOrganizations } from './useOrganizations';

/**
 * Component that handles automatic organization selection.
 * This should be placed high in the component tree, after authentication.
 */
export function OrgAutoSelector({ children }: { children: React.ReactNode }) {
  const { selectedOrg, setSelectedOrg } = useOrg();
  const { data, isLoading } = useUserOrganizations();
  const orgs = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const hasAutoSelected = useRef(false);

  useEffect(() => {
    // Skip if still loading or already has a selection
    if (isLoading || selectedOrg || hasAutoSelected.current) {
      return;
    }

    // Auto-select for single org users
    if (orgs.length === 1) {
      console.log('Auto-selecting single organization:', orgs[0].name);
      hasAutoSelected.current = true;
      setSelectedOrg(orgs[0]);
      return;
    }

    // For multiple orgs, check if we have a stored preference
    if (orgs.length > 1) {
      const storedOrgStr = localStorage.getItem('selectedOrg');
      if (storedOrgStr) {
        try {
          const storedOrg = JSON.parse(storedOrgStr);
          const matchingOrg = orgs.find((org) => org.id === storedOrg.id);
          if (matchingOrg) {
            console.log(
              'Restoring previously selected organization:',
              matchingOrg.name,
            );
            hasAutoSelected.current = true;
            setSelectedOrg(matchingOrg);
            return;
          }
        } catch (e) {
          console.error('Failed to parse stored org:', e);
        }
      }
    }
  }, [isLoading, orgs, selectedOrg, setSelectedOrg]);

  // Reset the flag when organizations change (e.g., user logs out and back in)
  useEffect(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      hasAutoSelected.current = false;
    }
  }, [data]);

  return <>{children}</>;
}
